import { type FormEvent, type PropsWithChildren, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingState } from "@/components/ui/loading-state";
import { buildBaseUrl, getTenantContext } from "@/lib/tenant";

interface GateResponse {
  data?: {
    enabled?: boolean;
    authorized?: boolean;
  };
  message?: string;
}

function isProtectedHost() {
  if (typeof window === "undefined") {
    return false;
  }

  return window.location.hostname === "staging.fanvault.io";
}

export function StagingAccessGate({ children }: PropsWithChildren) {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const apiBase = useMemo(() => {
    if (typeof window === "undefined") {
      return "";
    }

    return buildBaseUrl(getTenantContext(window.location));
  }, []);

  useEffect(() => {
    if (!isProtectedHost()) {
      setLoading(false);
      setEnabled(false);
      setAuthorized(true);
      return;
    }

    void loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  async function loadStatus() {
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${apiBase}/api/v1/staging-access/status`, {
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
        },
      });
      const payload = (await response.json()) as GateResponse;
      setEnabled(Boolean(payload.data?.enabled));
      setAuthorized(Boolean(payload.data?.authorized));
    } catch {
      setEnabled(true);
      setAuthorized(false);
      setMessage("Could not verify staging access. Retry in a moment.");
    } finally {
      setLoading(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!password) {
      setMessage("Enter the staging password.");
      return;
    }

    setSubmitting(true);
    setMessage("");

    try {
      const response = await fetch(`${apiBase}/api/v1/staging-access/login`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ password }),
      });
      const payload = (await response.json().catch(() => ({}))) as GateResponse;

      if (!response.ok) {
        setMessage(payload.message || "Incorrect password.");
        return;
      }

      setPassword("");
      setEnabled(Boolean(payload.data?.enabled));
      setAuthorized(Boolean(payload.data?.authorized));
    } catch {
      setMessage("Could not unlock staging right now.");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <LoadingState className="min-h-screen border-0 bg-transparent" label="Checking staging access..." />;
  }

  if (!enabled || authorized) {
    return <>{children}</>;
  }

  return (
    <div className="page-frame min-h-screen px-4 py-6 md:px-6 md:py-8">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-xl items-center">
        <div className="glass w-full rounded-[32px] p-8 shadow-[0_30px_90px_rgba(15,23,42,0.45)]">
          <p className="text-sm font-semibold uppercase tracking-[0.28em] text-slate-400">Staging Access</p>
          <h1 className="mt-3 text-3xl font-black text-white">Enter the staging password</h1>
          <p className="mt-3 text-sm leading-7 text-slate-300">
            This environment is restricted. Enter the shared staging password to continue into the app.
          </p>

          <form className="mt-8 space-y-4" onSubmit={submit}>
            <label className="block space-y-2">
              <span className="text-sm font-semibold text-slate-200">Password</span>
              <Input
                autoComplete="current-password"
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Staging password"
                type="password"
                value={password}
              />
            </label>

            {message ? <p className="text-sm text-rose-300">{message}</p> : null}

            <div className="flex items-center gap-3">
              <Button disabled={submitting} type="submit">
                {submitting ? "Unlocking..." : "Unlock staging"}
              </Button>
              <Button disabled={loading || submitting} onClick={() => void loadStatus()} type="button" variant="outline">
                Retry
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
